
import { Users, Clock, Navigation, X, Star } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogClose } from "@/components/ui/dialog";
import { formatDate } from "@/utils/dateUtils";
import { Button } from "@/components/ui/button";
import { getAssetPath } from "@/utils/assetUtils";
import TripActionButtons from "./trip/TripActionButtons";

interface CommunityEvent {
  name: string;
  date: string;
  event_type: string;
  address: {
    id: number;
    locality: {
      name: string;
      postal_code: string;
    };
    state: {
      name: string;
      code: string;
    };
    formatted: string;
  };
  distance_miles: number | null;
}

interface Address {
  id: number;
  locality: {
    name: string;
    postal_code: string;
  } | null;
  state: {
    name: string;
    code: string;
  } | null;
  formatted: string;
}

interface TripDetailProps {
  isOpen: boolean;
  onClose: () => void;
  community_event?: CommunityEvent;
  pickup_at: string;
  passenger_count?: number;
  organization_price?: number | string;
  booking_duration?: string | number;
  claimed_by: string | null;
  id: string;
  to_address?: Address;
  from_address?: Address;
  is_round_trip?: boolean;
  onClaimTrip?: (tripId: string) => void;
  rating?: number;
  rating_count?: number;
  status?: string;
  mobile_number?: string;
}

const TripDetail = ({
  isOpen,
  onClose,
  community_event,
  pickup_at,
  passenger_count = 0,
  organization_price = 1000,
  booking_duration = 0,
  claimed_by,
  id,
  to_address,
  from_address,
  is_round_trip,
  onClaimTrip,
  rating = 0,
  rating_count = 0,
  status,
  mobile_number
}: TripDetailProps) => {
  const formattedDate = formatDate(pickup_at);
  const isCompleted = status === "complete" || claimed_by === "completed";
  
  console.log(`TripDetail - Trip ID ${id} status: ${status}, claimed_by: ${claimed_by}, isCompleted: ${isCompleted}`);
  
  // Get the location/city name from to_address or community_event
  const location = to_address?.locality?.name || 
                  community_event?.address?.locality?.name || 
                  "Las Vegas";
  
  // Get the event name from community_event or use default
  const eventName = community_event?.name || "Event";
  
  // Format booking duration (could be string "HH:MM:SS" or number)
  const formatDuration = (duration: string | number): string => {
    if (typeof duration === 'string') {
      // If it's in format "HH:MM:SS", extract hours
      const match = duration.match(/^(\d+):/);
      return match ? `${match[1]} hours` : `${duration}`;
    }
    return `${duration} hours`;
  };
  
  const formattedDuration = formatDuration(booking_duration);
  
  // Format price (could be number or string with currency format)
  const formatPrice = (price: number | string): string => {
    if (typeof price === 'string') {
      return price;
    }
    return price.toString();
  };
  
  const formattedPrice = formatPrice(organization_price);
  
  // Determine if trip is one-way or round trip
  const tripType = is_round_trip ? "Round Trip" : "One Way";

  // Render stars for rating
  const renderStars = (rating: number) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;
    
    for (let i = 0; i < fullStars; i++) {
      stars.push(<Star key={`full-${i}`} size={16} className="text-green-400 fill-green-400" />);
    }
    
    if (hasHalfStar) {
      stars.push(<Star key="half" size={16} className="text-green-400 fill-green-400 opacity-50" />);
    }
    
    const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);
    for (let i = 0; i < emptyStars; i++) {
      stars.push(<Star key={`empty-${i}`} size={16} className="text-gray-400" />);
    }
    
    return stars;
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent 
        className={`p-0 max-w-sm rounded-lg overflow-hidden ${
          isCompleted ? 'bg-black text-white' : 'bg-white text-black'
        }`}
        style={{
          backgroundImage: isCompleted ? 
            `url(${getAssetPath("Completed_Trip_Card_Background.png")})` : 
            'none',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <DialogTitle className="sr-only">Trip Details</DialogTitle>
        <div className={`p-4 ${!isCompleted ? 'bg-gray-100' : ''}`}>
          <div className="flex justify-between items-center mb-2">
            <div className="flex items-center gap-2">
              <div className={`h-8 w-8 ${isCompleted ? 'bg-white text-black' : 'bg-black text-white'} rounded-full flex items-center justify-center`}>
                <span className="text-sm">🎵</span>
              </div>
              <span className={`${isCompleted ? 'text-white' : 'text-black'} font-bold text-lg`}>
                {eventName} {location}
              </span>
            </div>
            <DialogClose className={`rounded-full ${isCompleted ? 'bg-green-400 text-black' : 'bg-green-400 text-black'} px-4 py-1 text-sm font-medium`}>
              Close
            </DialogClose>
          </div>

          {!isCompleted && (
            <div className="flex justify-between mb-2">
              <div className="flex items-center gap-1 text-black text-sm">
                <Users size={16} />
                <span>{passenger_count} guests</span>
              </div>
              <div className="flex items-center gap-1 text-black text-sm">
                <Clock size={16} />
                <span>{formattedDuration}</span>
              </div>
              <div className="flex items-center gap-1 text-black text-sm">
                <Navigation size={16} />
                <span>{tripType}</span>
              </div>
            </div>
          )}

          <h3 className={`text-3xl font-bold mb-3 ${isCompleted ? 'text-white' : 'text-black'}`}>
            {isCompleted ? `${formattedDate} - Trip Complete` : formattedDate}
          </h3>
          
          <div className={`${isCompleted ? 'mt-2' : 'bg-black rounded-md py-2 px-3 inline-flex items-center gap-1'} mb-3`}>
            <span className={`${isCompleted ? 'text-green-400 block text-2xl font-semibold' : 'text-green-400 text-sm'}`}>
              {isCompleted ? 'Payout:' : 'Est Payout:'}
            </span>
            <span className={`${isCompleted ? 'text-white text-2xl font-semibold' : 'text-white text-lg font-medium'}`}>
              ${formattedPrice}
            </span>
          </div>

          {isCompleted && (
            <div className="mt-4">
              <p className="text-white text-sm mb-1">Rating</p>
              <div className="bg-gray-900 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <span className="text-white text-3xl font-bold">{rating.toFixed(1)}</span>
                  <div className="flex space-x-1">
                    {renderStars(rating)}
                  </div>
                </div>
                {rating_count > 0 && (
                  <div className="text-gray-400 text-xs text-right mt-1">
                    {rating_count} {rating_count === 1 ? 'rating' : 'ratings'}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {!isCompleted && (
          <div className="px-4 py-3">
            <h4 className="flex items-center gap-1 text-black font-medium mb-2">
              <Navigation size={16} />
              <span>Route</span>
            </h4>
            <div className="relative w-full h-48 bg-gray-200 rounded-lg mb-4 overflow-hidden">
              <img 
                src="public/lovable-uploads/74496745-d528-40a7-9080-1bbbdd7ed38d.png" 
                alt="Trip route map" 
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        )}

        <div className="px-4 pb-4">
          <TripActionButtons
            claimed_by={claimed_by}
            isCompleted={isCompleted}
            onClaimTrip={() => {
              onClaimTrip && onClaimTrip(id);
              onClose();
            }}
            tripId={id}
            mobile_number={mobile_number}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TripDetail;
